import { ComponentProps, useMemo } from 'react';
import { Source } from 'react-map-gl';
import { Feature, Point } from 'geojson';
import { TrailActivityType } from '@alltrails/trails';
import useMap from '../../hooks/useMap';
import { useSelector } from '../../redux';
import { serializedCoordinatesToCoordinates } from '../../utils/serializedCoordinates';
import TrailClusters from './TrailClusters';
import TrailPins from './TrailPins';
import TrailResultProperties from './TrailResultProperties';
import ActiveTrailPolylines from './ActiveTrailPolylines';

const sourceId = 'trail-results-source';

export type TrailResultsProps = {
  activityType?: TrailActivityType; // Used to display the correct trail card stats
  clusterMaxZoom?: number;
  clusterRadius?: number;
  preventClustering?: boolean;
} & Pick<ComponentProps<typeof TrailPins>, 'cardLocation' | 'detailedCardLocation' | 'mapLocation' | 'onTrailCardViewed'>;

// TrailResults is responsible for rendering trail pins on a map, based on the trailResults state variable.
// Ultimately, we render 2 layers of trail-related markers: TrailClusters (based on mapbox clustering)
// and TrailPins, which represent both single-trail trailheads and multi-trail trailheads.
// These two layers cannot be cleanly split because the mousemove handling gets messy when we have to consider multiple layers.
// While a more generic component may cut down on some lines of code if we ever need to support the same behavior
// for maps or recordings, I would advise caution in making the switch since having the non-generic components
// allows us to interact with redux without having to check the type of a generic "result" object, and cleanly divide
// the different map layers since we have a nicely defined Properties type used in the feature collection.
const TrailResults = ({
  activityType,
  clusterMaxZoom = 11,
  clusterRadius = 40,
  preventClustering,
  cardLocation,
  detailedCardLocation,
  mapLocation,
  onTrailCardViewed
}: TrailResultsProps) => {
  const map = useMap();
  const { trailResults } = useSelector(state => ({ trailResults: state.map.trailResults }));

  const features = useMemo(() => {
    if (!trailResults) {
      return [];
    }
    return Object.keys(trailResults).reduce<Feature<Point, TrailResultProperties>[]>((allFeatures, serializedCoordinates) => {
      const trails = trailResults[serializedCoordinates];
      if (!trails) {
        return allFeatures;
      }
      const { lat, lng } = serializedCoordinatesToCoordinates(serializedCoordinates);
      const isClosed = trails.every(trail => trail.is_closed);
      return [
        ...allFeatures,
        {
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: { isClosed, serializedCoordinates, trailCount: trails.length },
          type: 'Feature'
        }
      ];
    }, []);
  }, [trailResults]);

  const { trailCounts, closedTrailCounts } = useMemo(() => {
    const counts: Set<number> = new Set([]);
    const closedCounts: Set<number> = new Set([]);
    features.forEach(feature => {
      if (feature.properties.isClosed) {
        closedCounts.add(feature.properties.trailCount);
      } else {
        counts.add(feature.properties.trailCount);
      }
    });
    return { trailCounts: counts, closedTrailCounts: closedCounts };
  }, [features]);

  const dynamicClusterRadius = () => {
    if (!map) {
      return clusterRadius;
    }

    const zoom = map.getZoom();
    if (zoom <= 4) {
      return 10;
    } else if (zoom <= 8) {
      return 20;
    } else {
      return 40;
    }
  };

  if (!features.length) {
    return null;
  }

  return (
    <>
      <Source
        id={sourceId}
        type="geojson"
        data={{ type: 'FeatureCollection', features }}
        cluster={preventClustering ? false : true}
        clusterProperties={{
          clusterTotalCount: ['+', ['get', 'trailCount']]
        }}
        clusterMaxZoom={clusterMaxZoom} // https://docs.mapbox.com/mapbox-gl-js/style-spec/sources/#geojson-clusterMaxZoom
        clusterRadius={dynamicClusterRadius()} // https://docs.mapbox.com/mapbox-gl-js/style-spec/sources/#geojson-clusterRadius
      >
        <TrailPins
          activityType={activityType}
          cardLocation={cardLocation}
          detailedCardLocation={detailedCardLocation}
          onTrailCardViewed={onTrailCardViewed}
          mapLocation={mapLocation}
          sourceId={sourceId}
          trailCounts={trailCounts}
          closedTrailCounts={closedTrailCounts}
        />
        {!preventClustering && <TrailClusters sourceId={sourceId} />}
      </Source>
      <ActiveTrailPolylines />
    </>
  );
};

export default TrailResults;
